import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../../app/store";
import taskService from "./taskService";
import {
  AccessOptionForTaskDto,
  AssetLinkDto,
  ArchivedTaskDto,
  RestoredTaskDto,
  CreateAssetLinkDto,
  CreateTaskAccessDto,
  CreateTaskThunkArg,
  CreateTaskWithAccessDto,
  GetTaskDto,
  RecordedTimeDto,
  ReplaceTaskAccessDto,
  ReplaceTaskAccessResolveDto,
  StartNoteTimerDto,
  UpdateAssetLinkDto,
  UpdateTaskDto,
  GetArchivedNotes,
  UpdateRecordedTimeDto,
  FullTaskDto,
  GetActiveRecordingDto,
  RecordingInRangeDto,
  ReflectWeekDto,
  StartNoteRecordReturnDto,
  CreateManyTasksWithAccessDto,
  CreateManyTasksWithAccessResponseDto,
  DeleteTaskResponseDto,
} from "./types";

export const togglePauseTimer = createAsyncThunk<
  RecordedTimeDto,
  void,
  { state: RootState }
>("tasks/togglePauseTimer", async (_, thunkAPI) => {
  try {
    const resp = await taskService.togglePauseTimer();
    return resp;
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const startNoteRecord = createAsyncThunk<
  StartNoteRecordReturnDto,
  StartNoteTimerDto,
  { state: RootState }
>("tasks/startNoteRecord", async (recordData: StartNoteTimerDto, thunkAPI) => {
  try {
    const resp = await taskService.startNoteRecord(recordData);
    if (!resp.noteId) {
      throw Error();
    }
    return resp;
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateNoteRecord = createAsyncThunk<
  RecordedTimeDto,
  UpdateRecordedTimeDto,
  { state: RootState }
>(
  "tasks/updateNoteRecord",
  async (updatedData: UpdateRecordedTimeDto, thunkAPI) => {
    try {
      const resp = await taskService.updateNoteRecord(updatedData);
      return resp;
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const endNoteRecord = createAsyncThunk<
  RecordedTimeDto,
  string,
  { state: RootState }
>("tasks/endNoteRecord", async (noteId: string, thunkAPI) => {
  try {
    const resp = await taskService.endNoteRecord(noteId);
    return resp;
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const deleteNoteRecord = createAsyncThunk<
  string,
  { noteId: string; recordId: string },
  { state: RootState }
>("tasks/deleteNoteRecord", async ({ noteId, recordId }, thunkAPI) => {
  try {
    return await taskService.deleteNoteRecord({
      noteId: noteId,
      recordId: recordId,
    });
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getNoteTimerRecords = createAsyncThunk<
  RecordedTimeDto[],
  string,
  { state: RootState }
>("tasks/getNoteTimerRecords", async (noteId: string, thunkAPI) => {
  try {
    return await taskService.getNoteRecords(noteId);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

/////////////////////////////////////////////////////////////////////

export const createAssetData = createAsyncThunk<
  AssetLinkDto,
  CreateAssetLinkDto,
  { state: RootState }
>("tasks/createAssetData", async (assetData: CreateAssetLinkDto, thunkAPI) => {
  try {
    return (await taskService.createAssetLink(assetData)) as AssetLinkDto;
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateAssetLinkData = createAsyncThunk<
  AssetLinkDto,
  UpdateAssetLinkDto,
  { state: RootState }
>(
  "tasks/updateAssetLinkData",
  async (assetData: UpdateAssetLinkDto, thunkAPI) => {
    try {
      return (await taskService.updateAssetLinkData(assetData)) as AssetLinkDto;
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Tasks
/////////////////////////////////////////////////////////////////////////////////

export const updateTask = createAsyncThunk<
  GetTaskDto,
  UpdateTaskDto,
  { state: RootState }
>("tasks/updateTask", async (updateData: UpdateTaskDto, thunkAPI) => {
  try {
    return await taskService.updateTask(updateData);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(error);
    return thunkAPI.rejectWithValue(message);
  }
});

export const deleteTask = createAsyncThunk<
  DeleteTaskResponseDto,
  string,
  { state: RootState }
>("tasks/deleteTask", async (taskId: string, thunkAPI) => {
  try {
    return await taskService.deleteTask(taskId);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(error);
    return thunkAPI.rejectWithValue(message);
  }
});

// Finish active Tasks first!
export const finishTask = createAsyncThunk<
  DeleteTaskResponseDto,
  string,
  { state: RootState }
>("tasks/finishTask", async (taskId: string, thunkAPI) => {
  try {
    const finishedTask = await taskService.finishTask(taskId);

    return finishedTask;
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(error);
    return thunkAPI.rejectWithValue(message);
  }
});

export interface RestoreArchivedTaskResponse {
  task: RestoredTaskDto;
  archivedTaskId: string;
}

// Brings a finished or deleted task back as a new note. Handled in the task
// slice (lists, finished markers) and the calendar slice (slots).
export const restoreArchivedTask = createAsyncThunk<
  RestoreArchivedTaskResponse,
  string,
  { state: RootState }
>("tasks/restoreArchivedTask", async (archivedTaskId: string, thunkAPI) => {
  try {
    const finishedTask = await taskService.restoreArchivedTask(archivedTaskId);

    return { task: finishedTask, archivedTaskId };
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(error);
    return thunkAPI.rejectWithValue(message);
  }
});

export const createQuickTask = createAsyncThunk<
  GetTaskDto,
  void,
  { state: RootState }
>("tasks/createQuickTask", async (_, thunkAPI) => {
  try {
    const response = await taskService.createQuickTask();
    return response;
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const createTask = createAsyncThunk<
  GetTaskDto,
  CreateTaskThunkArg,
  { state: RootState }
>("tasks/createTask", async (arg, thunkAPI) => {
  try {
    // Strip internal options so nothing leaks to the API
    const { __opts, ...payload } = arg ?? {};
    const response = await taskService.createTask(
      payload as CreateTaskWithAccessDto,
    );
    return response;
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const createTasks = createAsyncThunk<
  CreateManyTasksWithAccessResponseDto,
  CreateManyTasksWithAccessDto,
  { state: RootState }
>("tasks/createTasks", async (arg, thunkAPI) => {
  try {
    // Strip internal options so nothing leaks to the API

    const response = await taskService.createTasks(arg);

    return { notesData: response, collectionId: arg.collectionId };
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

// Get user task
/**
 * @param taskId
 */
export const getTask = createAsyncThunk<
  GetTaskDto,
  string,
  { state: RootState }
>("tasks/getTask", async (taskId: string, thunkAPI) => {
  try {
    const task = await taskService.getTask(taskId);

    return task;
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const getActiveTask = createAsyncThunk<
  FullTaskDto | null,
  void,
  { state: RootState }
>("tasks/getActiveTask", async (_, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const taskId = state.user.userData.activeTask?.id;

    if (taskId) return await taskService.getTask(taskId);
    else return null;
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const getActiveRecording = createAsyncThunk<
  GetActiveRecordingDto | null,
  void,
  { state: RootState }
>("tasks/getActiveRecording", async (_, thunkAPI) => {
  try {
    return await taskService.getActiveRecording();
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

// Recordings (work + breaks) of the user in a time range, max 31 days
export const getRecordingsInRange = createAsyncThunk<
  RecordingInRangeDto[],
  { from: string; to: string },
  { state: RootState }
>("tasks/getRecordingsInRange", async ({ from, to }, thunkAPI) => {
  try {
    return await taskService.getRecordingsInRange(from, to);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.message || String(error));
  }
});

// Rückblick data of one week
export const getReflectWeek = createAsyncThunk<
  ReflectWeekDto,
  { from: string; to: string },
  { state: RootState }
>("tasks/getReflectWeek", async ({ from, to }, thunkAPI) => {
  try {
    return await taskService.getReflectWeek(from, to);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.message || String(error));
  }
});

// Get user tasks
export const getTasks = createAsyncThunk<
  GetTaskDto[],
  void,
  { state: RootState }
>("tasks/getTasks", async (_, thunkAPI) => {
  try {
    return await taskService.getTasks();
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getCompletedTasks = createAsyncThunk<
  GetArchivedNotes[],
  number | undefined,
  { state: RootState }
>("tasks/getCompletedTasks", async (startIndex, thunkAPI) => {
  try {
    return await taskService.getCompletedTasks(startIndex);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(error);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getArchivedTask = createAsyncThunk<
  ArchivedTaskDto,
  string,
  { state: RootState }
>("tasks/getArchivedTask", async (archivedTaskId, thunkAPI) => {
  try {
    return await taskService.getArchivedTask(archivedTaskId);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(error);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getDeletedTasks = createAsyncThunk<
  GetArchivedNotes[],
  undefined,
  { state: RootState }
>("tasks/getDeletedTasks", async (_, thunkAPI) => {
  try {
    return await taskService.getDeletedTasks();
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(error);
    return thunkAPI.rejectWithValue(message);
  }
});

export const initializeTasks = createAsyncThunk<
  void,
  void,
  { state: RootState }
>("tasks/initializeTasks", async (_, thunkAPI) => {
  const state = thunkAPI.getState();

  if (state.tasks.meta.taskDataInitialized) {
    console.log("Tasks are already initialized, skipping `getTasks()`.");
    return; // 🚀 Prevents redundant calls that could affect the socket
  }

  await thunkAPI.dispatch(getTasks());
});

// Get user tasks
export const createTaskAccess = createAsyncThunk<
  AccessOptionForTaskDto,
  CreateTaskAccessDto,
  { state: RootState }
>("tasks/createTaskAccess", async (createTaskAccessData, thunkAPI) => {
  try {
    const res = await taskService.createTaskAccess(createTaskAccessData);
    return res;
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

// Get user tasks
export const replaceTaskAccess = createAsyncThunk<
  ReplaceTaskAccessResolveDto,
  ReplaceTaskAccessDto,
  { state: RootState }
>("tasks/replaceTaskAccess", async (replaceTaskAccessData, thunkAPI) => {
  try {
    return {
      accessOption: await taskService.replaceTaskAccess(replaceTaskAccessData),
      deletedAccess: replaceTaskAccessData.deleteAccessId,
    };
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

// Get user tasks
export const deleteTaskAccess = createAsyncThunk<
  { accessId: string; noteId: string },
  { accessId: string; noteId: string },
  { state: RootState }
>("tasks/deleteTaskAccess", async (deleteAccessData, thunkAPI) => {
  try {
    const accessId = await taskService.deleteTaskAccess(
      deleteAccessData.accessId,
      deleteAccessData.noteId,
    );

    return {
      accessId,
      noteId: deleteAccessData.noteId,
    };
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

// Collection ROWs
